import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Plan,
  DrugCosts,
  DrugCostInfo,
  DrugFootnote,
  DrugCostCoverageReason,
  PrescriptionDrug,
  PlanDetailsPageSection,
  ActionType,
} from "../../../@types";
import {
  displayDrug,
  ifNumber,
  planDataUtils,
  shouldSuppressDrugPricing,
  useTranslate,
  useTableCaption,
  addRowAttributes,
  isMaPlanType,
  planHasDrugCoverage,
  makePlanLongId,
} from "../../../helpers";
import { AppStore } from "../../../app/store";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import {
  CollapsibleBox,
  Footnote,
  FootnoteMarker,
  Footnotes,
  RenderedTable,
} from "../../../components";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Alert,
  CloseIconThin,
  HelpDrawer,
  HelpDrawerToggle,
} from "@cmsgov/ds-medicare-gov";
import { AugmentedColumn } from "../../../@types/react-table";
import { sumBy, useGetPharmacyName } from "./drugCoverageHelpers";
import { CellValue, TableInstance } from "react-table";
import { GlobalSelectFilterUI } from "../../../components/Tables/GlobalSelectFilterUI";
import { Routes } from "../../../app";
import { Link, useLocation } from "react-router-dom";
import messages from "../../../translations/en-US.json";
import {
  AnalyticsActionType,
  Ga4Event,
  Ga4ParentComponentType,
  Ga4EventType,
} from "../../../app/contexts/Analytics/types";

const tableTitleKey = "plan_details.drug_costs.estimated_coverage";
const tableTitle = messages[tableTitleKey];

const accessors = {
  drugs: "drugs",
  retail: "retail",
  before_deductible: "before_deductible",
  after_deductible: "after_deductible",
  in_coverage_gap: "in_coverage_gap",
  after_coverage_gap: "after_coverage_gap",
};

const {
  drugs,
  retail,
  before_deductible,
  after_deductible,
  in_coverage_gap,
  after_coverage_gap,
} = accessors;

interface DrugCostFootnote {
  ndcs: string[];
  text: string | JSX.Element;
  type: DrugFootnote;
}

interface CostsByCoverageDataInterface {
  drugs: JSX.Element | string;
  retail: string;
  before_deductible?: string;
  after_deductible: string;
  in_coverage_gap: string;
  after_coverage_gap: string;
}

export const CostsByCoveragePhaseTable: React.FC<{
  drugCostInfo: DrugCostInfo;
  plan: Plan;
}> = ({ drugCostInfo, plan }) => {
  const t = useTranslate();
  const flags = useFlags();
  const location = useLocation();
  const getTableCaption = useTableCaption({
    genericElement: true,
  });
  const {
    state: { prescriptions, language, trackedEvents },
    state,
    dispatch,
  } = useContext(AppStore.AppContext);
  const {
    mctCurrentPlanYear,
    tmpFeEnableIraVaccineFootnote,
    tmpFeEnableIraInsulinFootnote,
  } = flags;
  const { contract_year: year } = plan;
  const planLongId = makePlanLongId(drugCostInfo.plan);
  const [footnoteMarkerNdcs, setFootnoteMarkerNdcs] = useState<
    Record<string, string>
  >({});
  const [
    renderedIraVaccineFootnotes,
    setRenderedIraVaccineFootnotes,
  ] = useState<Record<string, boolean>>({});
  const [
    renderedIraInsulinFootnotes,
    setRenderedIraInsulinFootnotes,
  ] = useState<Record<string, boolean>>({});
  const [globalFootnotes, setGlobalFootnotes] = useState<DrugCostFootnote[]>(
    []
  );
  const [pharmacyFootnotes, setPharmacyFootnotes] = useState<{
    [npi: string]: DrugCostFootnote[];
  }>({});
  const [showCoveragePhasesHelp, setShowCoveragePhasesHelp] = useState<boolean>(
    false
  );
  const [selectOptions, setSelectOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [filterValue, setFilterValue] = useState<string>("");

  // Helper functions - Ported from old design and not yet updated with comments
  // @TODO - add comments
  const isDrugSuppression = shouldSuppressDrugPricing(plan.redactions);

  const checkForSuppression = (val: number | string): string => {
    if (isDrugSuppression) {
      return t("na");
    } else {
      return ifNumber(val);
    }
  };

  const drugDeductible = planDataUtils.getAnnualDrugDeductible(
    state,
    mctCurrentPlanYear,
    plan
  );

  const hasDeductible = drugDeductible !== "$0.00";

  const getDescriptionByYear = (
    descriptionKey: string,
    year: string
  ): string => {
    return t(`${descriptionKey}.${year}`);
  };

  const OON_FOOTNOTE_SUPPRESSION_LIST: DrugFootnote[] = [
    DrugFootnote.EXCLUDED_DRUG,
    DrugFootnote.PARTIAL_GAP,
    DrugFootnote.FREE_FIRST_FILL,
  ];

  const filterOutDisallowedFootnotes = (
    pharmacy: DrugCosts,
    globalFootnotes: DrugCostFootnote[]
  ): DrugCostFootnote[] =>
    globalFootnotes.filter(footnote => {
      // filter out the limited distribution footnote for out-of-network pharmacy because if shown,
      // the footer is hidden (due to "bug") and this would look weird.
      if (
        !pharmacy.in_network &&
        (OON_FOOTNOTE_SUPPRESSION_LIST.includes(footnote.type) ||
          footnote.type == DrugFootnote.LIMITED_DISTRIBUTION)
      ) {
        return false;
      }
      return true;
    });

  // show the footnote superscript next to the drug name in first column.
  const showFootnoteSuperscripts = (
    pharmacy: DrugCosts,
    ndc: string
  ): JSX.Element | null => {
    const markers = [
      ...filterOutDisallowedFootnotes(pharmacy, globalFootnotes),
      ...(pharmacyFootnotes[pharmacy.npi] ?? []),
    ]
      .map((f, i) => (f.ndcs.includes(ndc) ? i + 1 : false))
      .filter(m => !!m)
      .map((m, i) => {
        const marker = String(m);
        if (!footnoteMarkerNdcs[marker]) {
          setFootnoteMarkerNdcs(currentFootnoteMarkerNdcs => ({
            ...currentFootnoteMarkerNdcs,
            [marker]: ndc,
          }));
        }
        return (
          <FootnoteMarker
            key={i}
            marker={marker}
            id={`note-${pharmacy.npi || "mail-order"}-${i}`}
          />
        );
      });
    return markers.length > 0 ? <>{markers}</> : null;
  };

  // Runs on mount and whenever drugCostInfo updates
  useEffect(() => {
    const { restrictions, excluded_drugs, costs } = drugCostInfo;

    const pharmacyNotes: {
      [npi: string]: DrugCostFootnote[];
    } = {};

    const excluded = excluded_drugs;
    const freeFills = restrictions.filter(d => d.free_first_fill);
    const partialGaps = restrictions.filter(d => d.partial_gap);
    const limitedDistros = restrictions.filter(r => r.limited_distribution);
    const vaccines = restrictions.filter(r => r.vaccine);
    const insulins = restrictions.filter(r => r.insulin_price_cap);

    const drugDisplayNames: string[] = [];
    costs.forEach((cost, costIndex) => {
      const { npi, drug_costs } = cost;

      const notInFormularyNdcs: string[] = [];
      const defaultPriceUsedNdcs: string[] = [];
      const frequencyNotCoveredNdcs: string[] = [];

      if (costIndex === 0) {
        cost.drug_costs.forEach(cost =>
          drugDisplayNames.push(
            displayDrug(
              t,
              prescriptions.find(p => p.ndc === cost.ndc) ||
                ({} as PrescriptionDrug)
            )
          )
        );
      }

      drug_costs.forEach(drugCost => {
        const { ndc, coverage_reason, default_price_used, covered } = drugCost;

        if (coverage_reason === DrugCostCoverageReason.NOT_IN_FORMULARY) {
          notInFormularyNdcs.push(ndc);
        } else if (
          coverage_reason === DrugCostCoverageReason.COVERED &&
          default_price_used
        ) {
          defaultPriceUsedNdcs.push(ndc);
        } else if (
          coverage_reason === DrugCostCoverageReason.FREQUENCY_NOT_COVERED &&
          !covered &&
          default_price_used
        ) {
          frequencyNotCoveredNdcs.push(ndc);
        }
      });

      pharmacyNotes[npi] = [];

      if (notInFormularyNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: notInFormularyNdcs,
          text: t("drug_footnote.not_in_formulary"),
          type: DrugFootnote.NOT_IN_FORMULARY,
        });
      }

      if (defaultPriceUsedNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: defaultPriceUsedNdcs,
          text: t("drug_footnote.default_price_used"),
          type: DrugFootnote.DEFAULT_PRICE,
        });
      }

      if (frequencyNotCoveredNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: frequencyNotCoveredNdcs,
          text: t("drug_footnote.frequency_not_covered"),
          type: DrugFootnote.FREQUENCY_NOT_COVERED,
        });
      }
    });

    // Populate the filter for mobile
    setSelectOptions([
      ...drugDisplayNames.map(name => ({
        label: name,
        value: name,
      })),
    ]);

    const notes: DrugCostFootnote[] = [];

    if (excluded.length) {
      notes.push({
        ndcs: excluded,
        text: t("excluded_drugs_footnote"),
        type: DrugFootnote.EXCLUDED_DRUG,
      });
    }

    if (freeFills.length) {
      notes.push({
        ndcs: freeFills.map(x => x.ndc),
        text: t("drug_footnote.free_first_fill"),
        type: DrugFootnote.FREE_FIRST_FILL,
      });
    }

    if (partialGaps.length) {
      notes.push({
        ndcs: partialGaps.map(x => x.ndc),
        text: t("drug_footnote.partial_gap"),
        type: DrugFootnote.PARTIAL_GAP,
      });
    }

    if (limitedDistros.length) {
      notes.push({
        ndcs: limitedDistros.map(x => x.ndc),
        text: t("drug_footnote.limited_distribution"),
        type: DrugFootnote.LIMITED_DISTRIBUTION,
      });
    }

    if (
      vaccines.length &&
      tmpFeEnableIraVaccineFootnote &&
      plan.contract_year === "2023"
    ) {
      notes.push({
        ndcs: vaccines.map(x => x.ndc),
        text: t("drug_footnote.ira.vaccine"),
        type: DrugFootnote.IRA_VACCINE,
      });
    }

    if (insulins.length && tmpFeEnableIraInsulinFootnote) {
      notes.push({
        ndcs: insulins.map(x => x.ndc),
        text: t("drug_footnote.ira.insulin"),
        type: DrugFootnote.IRA_INSULIN,
      });
    }

    setGlobalFootnotes(notes);
    setPharmacyFootnotes(pharmacyNotes);
  }, [
    drugCostInfo,
    language,
    tmpFeEnableIraInsulinFootnote,
    tmpFeEnableIraVaccineFootnote,
  ]);

  // Set up and render help drawer
  const helpDrawerContent = (
    <div className="mct-c-help-drawer">
      <HelpDrawer
        heading={t("plan_terms.learn_more_about_coverage_phases")}
        onCloseClick={(): void => setShowCoveragePhasesHelp(false)}
        closeButtonText={<CloseIconThin />}
        ariaLabel={t("plan_terms.close_coverage_phases_help_drawer")}
      >
        {t("plan_terms.coverage_phases_help.overview")}
        <dl>
          <p>
            <dt className="ds-u-display--inline ds-u-font-weight--bold">
              {t("plan_terms.coverage_phases_help.initial_coverage")}
            </dt>
            <span> - </span>
            <dd className="ds-u-margin--0 ds-u-display--inline">
              {getDescriptionByYear(
                "plan_terms.coverage_phases_help.initial_coverage_description",
                year
              )}
            </dd>
          </p>

          <p>
            <dt className="ds-u-display--inline ds-u-font-weight--bold">
              {t("plan_terms.coverage_phases_help.coverage_gap")}{" "}
              {t("plan_terms.coverage_phases_help.coverage_gap_aside")}
            </dt>
            <span> - </span>
            <dd className="ds-u-margin--0 ds-u-display--inline">
              {getDescriptionByYear(
                "plan_terms.coverage_phases_help.coverage_gap_description",
                year
              )}
            </dd>
          </p>

          <p>
            <dt className="ds-u-display--inline ds-u-font-weight--bold">
              {t("plan_terms.coverage_phases_help.catastrophic_coverage")}{" "}
              {t("plan_terms.coverage_phases_help.catastrophic_coverage_aside")}
            </dt>
            <span> - </span>
            <dd className="ds-u-margin--0 ds-u-display--inline">
              {getDescriptionByYear(
                "plan_terms.coverage_phases_help.catastrophic_coverage_description",
                year
              )}
            </dd>
          </p>
        </dl>
      </HelpDrawer>
    </div>
  );

  // Analytics
  // Store reference to IRA vaccine or IRA insulin footnotes when rendered, to be tracked later (below)
  const trackIraDrugFootnoteRender = (
    footnote: DrugCostFootnote,
    markerIndex: number
  ) => {
    if (
      footnote.type === DrugFootnote.IRA_VACCINE ||
      footnote.type === DrugFootnote.IRA_INSULIN
    ) {
      const ndc = footnoteMarkerNdcs[`${markerIndex}`];
      const drugName = displayDrug(
        name => name,
        prescriptions.find(p => p.ndc === ndc) || ({} as PrescriptionDrug)
      );
      if (ndc && drugName) {
        if (footnote.type === DrugFootnote.IRA_INSULIN) {
          if (!renderedIraInsulinFootnotes[drugName]) {
            setRenderedIraInsulinFootnotes(
              previouslyRenderedIraInsulinFootnotes => ({
                ...previouslyRenderedIraInsulinFootnotes,
                [drugName]: true,
              })
            );
          }
        } else if (footnote.type === DrugFootnote.IRA_VACCINE) {
          if (!renderedIraVaccineFootnotes[drugName]) {
            setRenderedIraVaccineFootnotes(
              previouslyRenderedIraVaccineFootnotes => ({
                ...previouslyRenderedIraVaccineFootnotes,
                [drugName]: true,
              })
            );
          }
        }
      }
    }
  };

  // Track footnotes viewed when one of the Estimated costs by coverage phases tables is
  // revealed (the pharmacy name heading is clicked to open a collapsed section)
  const sendIraDrugFootnoteImpression = () => {
    const trackedIraVaccineFootnoteImpressions =
      trackedEvents[Ga4Event.IRA_VACCINE_IMPRESSION] || {};
    const trackedIraInsulinFootnoteImpressions =
      trackedEvents[Ga4Event.IRA_INSULIN_IMPRESSION] || {};
    const vaccinesNotTrackedMap = {};
    const insulinsNotTrackedMap = {};
    Object.keys(renderedIraVaccineFootnotes).forEach(vaccineName => {
      if (!trackedIraVaccineFootnoteImpressions[vaccineName]) {
        vaccinesNotTrackedMap[vaccineName] = true;
      }
    });
    Object.keys(renderedIraInsulinFootnotes).forEach(insulinName => {
      if (!trackedIraInsulinFootnoteImpressions[insulinName]) {
        insulinsNotTrackedMap[insulinName] = true;
      }
    });
    const vaccinesNotTracked = Object.keys(vaccinesNotTrackedMap);
    const insulinsNotTracked = Object.keys(insulinsNotTrackedMap);

    if (vaccinesNotTracked.length > 0) {
      const vaccineNames = vaccinesNotTracked.join(",");
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.IRA_VACCINE_IMPRESSION,
          event_type: Ga4EventType.NON_INTERACTION,
          mct_vaccine_drug_name: vaccineNames,
          parent_component_heading: tableTitle,
          parent_component_type: Ga4ParentComponentType.TABLE,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "ira vaccine impression",
          event_label: vaccineNames,
        },
      });
      dispatch({
        type: ActionType.EVENT_TRACKED,
        payload: {
          event: Ga4Event.IRA_VACCINE_IMPRESSION,
          value: {
            ...trackedIraVaccineFootnoteImpressions,
            ...vaccinesNotTrackedMap,
          },
        },
      });
    }
    if (insulinsNotTracked.length > 0) {
      const insulinNames = insulinsNotTracked.join(",");
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.IRA_INSULIN_IMPRESSION,
          event_type: Ga4EventType.NON_INTERACTION,
          mct_insulin_drug_name: insulinNames,
          parent_component_heading: tableTitle,
          parent_component_type: Ga4ParentComponentType.TABLE,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "ira insulin impression",
          event_label: insulinNames,
        },
      });
      dispatch({
        type: ActionType.EVENT_TRACKED,
        payload: {
          event: Ga4Event.IRA_INSULIN_IMPRESSION,
          value: {
            ...trackedIraInsulinFootnoteImpressions,
            ...insulinsNotTrackedMap,
          },
        },
      });
    }
  };

  // Define the table caption
  const caption = {
    title: t(tableTitleKey),
    // eslint-disable-next-line react/display-name
    render: (title: JSX.Element) => (
      <>
        {title}
        <p>{t("plan_details.drug_costs.estimated_coverage_info")}</p>
        <HelpDrawerToggle
          helpDrawerOpen={showCoveragePhasesHelp}
          showDrawer={(): void => {
            setShowCoveragePhasesHelp(true);
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Help Drawer",
                event_label: "Learn More about Coverage Phases",
              },
            });
          }}
        >
          {t("plan_terms.learn_more_about_coverage_phases")}
        </HelpDrawerToggle>
        {showCoveragePhasesHelp && helpDrawerContent}
      </>
    ),
  };

  // Table Columns - Initial values, to be augmented per pharmacy
  // Must be an array of arrays, to avoid reuse of the the same "template"
  const costByCoverageColsSource: AugmentedColumn[][] = Array.from(
    // Use maximum number of pharmacies to create copies of this config
    // Closure inside config array setup made it difficult to reuse a single template
    { length: 5 },
    () => [
      {
        // @TODO, confirm we don't want this header
        // Header: t("plan_details.drug_costs.selected_drugs"),
        accessor: drugs,
      },
      {
        Header: t("plan_details.drug_costs.retail_cost"),
        accessor: retail,
      },
      {
        Header: t("plan_details.drug_costs.before_deductible"),
        accessor: before_deductible,
      },
      {
        Header: t("plan_details.drug_costs.after_deductible"),
        accessor: after_deductible,
      },
      {
        Header: t("plan_details.drug_costs.cost_in_coverage_gap"),
        accessor: in_coverage_gap,
      },
      {
        Header: t("plan_details.drug_costs.after_coverage_gap"),
        accessor: after_coverage_gap,
      },
    ]
  );

  /**
   * Set up the react-table configs
   * This returns an array of configs for each pharmacy table, both columns and data
   */
  const costByCoveragesConfigs: {
    cols: AugmentedColumn[];
    data: CostsByCoverageDataInterface[];
  }[] = drugCostInfo.costs
    .filter(pharmacy => !(pharmacy.mail_order && !hasMailOrderPharmacy(state)))
    .map((pharmacy, pharmacyIndex) => {
      // Check for footnotes and filter so that in "mobile" view, footnotes
      // only show for the currently filtered drug, or all if there is no
      // filter applied
      const thisPharmacyFootnotes = !pharmacyFootnotes[pharmacy.npi]
        ? []
        : pharmacyFootnotes[pharmacy.npi].filter(f => {
            // Return true for this footnote (don't filter it out) if
            // one or more of the pharmacy's drug-cost NDCs is within the
            // footnote's ndcs list
            const ndcMatches = pharmacy.drug_costs.filter(cost =>
              f.ndcs.includes(cost.ndc)
            );
            const drugDisplayNames = ndcMatches.map(drug =>
              displayDrug(
                t,
                prescriptions.find(p => p.ndc === drug.ndc) ||
                  ({} as PrescriptionDrug)
              )
            );
            // The display names of the matched NDCs (above) include the current
            // filter value, or there is currently no filter applied
            const nameMatch = drugDisplayNames.some(
              name => filterValue === "" || name === filterValue
            );
            return nameMatch;
          });
      const footNotes = [
        ...filterOutDisallowedFootnotes(pharmacy, globalFootnotes),
        ...thisPharmacyFootnotes,
      ];
      let colsSource = costByCoverageColsSource[pharmacyIndex]
        .filter(col => {
          if (!hasDeductible && col.accessor === "before_deductible") {
            return false;
          } else {
            return col;
          }
        })
        .map((col, colIndex) => {
          col.Cell = ({ value, row }: Partial<TableInstance> | CellValue) => {
            if (colIndex === 0) {
              pharmacy.drug_costs.forEach((cost, costIndex) => {
                // Test that the row represents a single drug
                // All rows except the last represent individual drugs
                if (row.index === costIndex && !row?.attributes?.key) {
                  addRowAttributes(row, {
                    ["key"]: `${cost.ndc}-${pharmacyIndex}-${row.index}`,
                  });
                }
              });
              if (row.index + 1 > pharmacy.drug_costs.length) {
                addRowAttributes(row, {
                  ["className"]: "mct-c-table__row--display-lg",
                });
              }
            }
            return value;
          };
          return col;
        })
        .map((col, colIndex) => {
          if (footNotes.length > 0 && colIndex === 0) {
            col.Footer = (
              <Footnotes
                className="pharmacy-cost-footnotes"
                id={`pharmacy-${pharmacy.npi || "mail-order"}-cost-footnotes`}
              >
                {footNotes.map((footnote, i) => {
                  const markerIndex: number = i + 1;
                  trackIraDrugFootnoteRender(footnote, markerIndex);
                  return (
                    <Footnote
                      id={`note-${pharmacy.npi || "mail-order"}-${i}`}
                      key={i}
                      text={footnote.text}
                      marker={String(markerIndex)}
                    />
                  );
                })}
              </Footnotes>
            );
          }
          return col;
        });
      // Add a not-in-network alert to any table that needs it, in a top-level,
      // table-spanning column header
      if (!pharmacy.in_network) {
        colsSource = [
          {
            accessor: "out_of_network_pharmacy",
            columns: colsSource,
            Header: (
              <Alert hideIcon={true} variation="warn">
                {isMaPlanType(plan.plan_type)
                  ? t("plan_details.drug_costs.out_of_network_warning_ma")
                  : t("plan_details.drug_costs.out_of_network_warning")}
                {planHasDrugCoverage(plan) && (
                  <>
                    &nbsp;
                    <Link
                      to={{
                        pathname: `${Routes.inNetworkPharmacy.replace(
                          ":id",
                          planLongId
                        )}`,
                        state: {
                          nextRoute: {
                            ...location,
                            hash: PlanDetailsPageSection.DRUG_COVERAGE,
                          },
                        },
                      }}
                    >
                      {t("plan_details.pharmacies.find_an_in_network_pharmacy")}
                    </Link>
                  </>
                )}
              </Alert>
            ),
            attributes: {
              alert: true,
            },
          },
        ];
      }
      const dataSource = pharmacy.drug_costs.map(cost => {
        const drugDisplayName = displayDrug(
          t,
          prescriptions.find(p => p.ndc === cost.ndc) ||
            ({} as PrescriptionDrug)
        );
        const data: CostsByCoverageDataInterface = {
          drugs: (
            <>
              <span
                className="mct-c-table__header-title"
                data-row-title={drugDisplayName}
              >
                {drugDisplayName}{" "}
              </span>
              {showFootnoteSuperscripts(pharmacy, cost.ndc)}
            </>
          ),
          retail: checkForSuppression(cost.full_cost),
          after_deductible: checkForSuppression(cost.initial_cost),
          in_coverage_gap: checkForSuppression(cost.gap_cost),
          after_coverage_gap: checkForSuppression(cost.catastrophic_cost),
        };
        if (hasDeductible)
          data.before_deductible = checkForSuppression(cost.deductible_cost);
        return data;
      });
      const totals: CostsByCoverageDataInterface = {
        drugs: t("plan_details.drug_costs.monthly_totals"),
        retail: checkForSuppression(sumBy(pharmacy.drug_costs, "full_cost")),
        after_deductible: checkForSuppression(
          sumBy(pharmacy.drug_costs, "initial_cost")
        ),
        in_coverage_gap: checkForSuppression(
          sumBy(pharmacy.drug_costs, "gap_cost")
        ),
        after_coverage_gap: checkForSuppression(
          sumBy(pharmacy.drug_costs, "catastrophic_cost")
        ),
      };
      if (hasDeductible) {
        totals.before_deductible = checkForSuppression(
          sumBy(pharmacy.drug_costs, "deductible_cost")
        );
      }
      dataSource.push(totals);
      return {
        cols: colsSource,
        data: dataSource,
      };
    });

  // Memoize configs
  const costByCoverageCols = useMemo(
    () => costByCoveragesConfigs.map(config => config.cols),
    [drugCostInfo, t, filterValue]
  );
  const costByCoverageData = (useMemo(
    () => costByCoveragesConfigs.map(config => config.data),
    [drugCostInfo, t, filterValue]
  ) as unknown) as Record<string, unknown>[][];

  /**
   * Sets the drug to whatever's currently in the select
   * The button that triggers is disabled if no drug is selected
   */
  const updateFilterValue = (filterValue: string) => {
    setFilterValue(filterValue);
  };

  return (
    <>
      {getTableCaption(caption)}
      <div className="ds-u-lg-display--none mct-hide-print">
        <GlobalSelectFilterUI
          options={selectOptions}
          filterConfig={{
            label: t("plan_details.costs_by_coverage_phase.filter_label"),
            buttonLabel: t(
              "plan_details.costs_by_coverage_phase.change_filter"
            ),
          }}
          onUpdateFilter={updateFilterValue}
        />
      </div>

      {drugCostInfo.costs
        .filter(
          pharmacy => !(pharmacy.mail_order && !hasMailOrderPharmacy(state))
        )
        .map((pharmacy, i) => {
          const headingId = `coverage_phases_${pharmacy.npi}`;
          const label = (
            <>
              <h4
                id={headingId}
                className="mct-c-table__caption-title ds-u-margin--0 ds-u-display--inline"
              >
                {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
                {useGetPharmacyName(pharmacy.npi)}
              </h4>
              <span className="ds-u-text-transform--uppercase ds-u-display--none ds-u-lg-display--inline-block">
                &nbsp;-&nbsp;
                {t("plan_details.drug_costs.coverage_levels")}
              </span>
            </>
          );

          return (
            <CollapsibleBox
              label={label}
              key={pharmacy.npi}
              open={false}
              titleClassname="ds-u-padding--3 ds-u-fill--gray-lightest mct-c-table__collapsible-title"
              transitionTime={1}
              className="mct-c-table__collapsible-container"
              plusMinusIcon={true}
              iconPosition="left"
              onOpen={sendIraDrugFootnoteImpression}
            >
              <RenderedTable
                key={pharmacy.npi}
                tableColumns={costByCoverageCols[i]}
                tableData={costByCoverageData[i]}
                filterValue={filterValue}
                rowHeaders
                borderless
                stackableBreakpoint="lg"
                className="CostsByCoveragePhaseTable"
                autoResetGlobalFilter={false}
                aria-labelledby={headingId}
              />
            </CollapsibleBox>
          );
        })}
    </>
  );
};
